/**
 * GestureSemantics — Traduce gestos médicos en hallazgos clínicos.
 *
 * Cuando el alumno aplica AUSCULTATION, PALPATION o PERCUSSION sobre un nodo,
 * este módulo consulta la tabla GESTURE_FINDINGS. Si el gesto es compatible
 * con el sistema anatómico del nodo target, agrega un nuevo nodo al paciente
 * (ej. "R3 gallop", "puñopercusión +") y emite FINDING_REVEALED para que el
 * ReasoningEngine recalcule patrones y triage.
 *
 * Si el gesto no es compatible, responde con una narrativa negativa
 * ("sin hallazgos") — el alumno aprende que no toda maniobra aporta.
 */

import state from '../core/state';
import { Patient } from './Patient';
import { PatientLoader } from '../data/PatientLoader';
import { Gesture, GESTURE_FINDINGS } from '../education/gesture-findings';
import { AnatomicalSystem } from '../types/medical';

export class GestureSemantics {
  private patient: Patient | null = null;
  private caseId: string | null = null;
  private patientLoader = new PatientLoader();

  bind(patient: Patient | null, caseId: string | null): void {
    this.patient = patient;
    this.caseId = caseId;
  }

  /**
   * Aplica un gesto sobre un nodo. Retorna true si reveló un hallazgo.
   * Si el nodo target no tiene sistema anatómico o el gesto no tiene entrada
   * en la tabla para ese (caseId, gesto, sistema), no hace nada visible.
   */
  apply(gesture: Gesture, targetNodeId: string): boolean {
    if (!this.patient || !this.caseId) return false;

    const targetNode = this.patient.getNode(targetNodeId);
    if (!targetNode) return false;

    const system = targetNode.data.system as AnatomicalSystem | undefined;
    if (!system) {
      this.notifyNoFinding(gesture, targetNode.data.concept.display);
      return false;
    }

    const finding = GESTURE_FINDINGS[this.caseId]?.[gesture]?.[system];
    if (!finding) {
      this.notifyNoFinding(gesture, targetNode.data.concept.display);
      return false;
    }

    // Evita duplicar el mismo hallazgo si el alumno repite el gesto.
    if (this.patient.getNode(finding.node.id)) {
      targetNode.pulse();
      return false;
    }

    // Normaliza valores opcionales y agrega al grafo.
    const normalized = this.patientLoader['normalizeNode'](finding.node);
    this.patient.addNode(normalized);
    const newNode = this.patient.getNode(normalized.id);
    if (newNode) {
      newNode.createLabel();
      newNode.label!.visible = state.ui.showLabels;
      newNode.revealBurst();
    }

    // Propaga al bus: ReasoningEngine recalcula, DifferentialPanel reajusta.
    state.emitFindingRevealed(normalized.id, 'gesture');

    state.addNotification({
      type: 'info',
      title: this.gestureTitle(gesture),
      message: finding.narrative,
      duration: 7000,
    });

    return true;
  }

  private gestureTitle(gesture: Gesture): string {
    switch (gesture) {
      case 'auscultation': return '🩺 Auscultación';
      case 'palpation':    return '👋 Palpación';
      case 'percussion':   return '👆 Percusión';
    }
  }

  private notifyNoFinding(gesture: Gesture, nodeLabel: string): void {
    state.addNotification({
      type: 'info',
      title: this.gestureTitle(gesture),
      message: `Sin hallazgos relevantes sobre ${nodeLabel}.`,
      duration: 3500,
    });
  }
}

export default GestureSemantics;
